import React, { useState, useEffect, useRef } from 'react';
import {
  Database, FileText, FolderTree, RefreshCw, Play,
  AlertTriangle, CheckCircle, Clock, Search, Filter,
  ChevronDown, ChevronRight, Eye, GitCompare, Server, Table2,
  MessageSquare, Zap, Lightbulb, Brain, Send, Copy, Loader, XCircle, ExternalLink
} from 'lucide-react';
import MigrationTab from './MigrationTab';
import InstanceDeploymentTab from '../InstanceDeployment/InstanceDeployment';
import toast from 'react-hot-toast';
import { getBrandName } from '../../config/branding';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import api from '../../utils/api';

const KnowledgeBaseAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState([]);
  const [files, setFiles] = useState([]);
  const [changedFiles, setChangedFiles] = useState(null);
  const [newFiles, setNewFiles] = useState(null);
  const [platformOverview, setPlatformOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [infrastructure, setInfrastructure] = useState(null);
  const [infraGenerating, setInfraGenerating] = useState(false);

  // AI Inference state
  const [inferenceSubTab, setInferenceSubTab] = useState('chat');
  const [inferenceLoading, setInferenceLoading] = useState(false);
  const [inferenceError, setInferenceError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [inferenceModules, setInferenceModules] = useState([]);
  const [inferenceSearchQuery, setInferenceSearchQuery] = useState('');
  const [inferenceSearchResults, setInferenceSearchResults] = useState(null);
  const [inferenceSearchType, setInferenceSearchType] = useState('all');
  const [impactFiles, setImpactFiles] = useState('');
  const [impactResult, setImpactResult] = useState(null);
  const [featureDescription, setFeatureDescription] = useState('');
  const [targetModule, setTargetModule] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [selectedInferenceModule, setSelectedInferenceModule] = useState(null);
  const [inferenceModuleDetails, setInferenceModuleDetails] = useState(null);
  const chatEndRef = useRef(null);

  const authHeaders = {
    'Authorization': `Bearer ${Cookies.get('sankalvax_token')}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchStats();
    fetchModules();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/kb/stats', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching KB stats:', error);
      toast.error('Failed to load KB statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch('/api/kb/modules', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setModules(data.modules);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      let url = '/api/kb/files?limit=100';
      if (filterModule) url += `&module=${filterModule}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const response = await fetch(url, { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchChangedFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/kb/changed-files', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setChangedFiles(data);
        toast.success(`Found ${data.summary.changed} changed files`);
      }
    } catch (error) {
      console.error('Error checking changed files:', error);
      toast.error('Failed to check for changes');
    } finally {
      setLoading(false);
    }
  };

  const reanalyzeChangedFiles = async () => {
    if (!window.confirm('Re-analyze only changed files? This will mark them as stale and run Phase 1 analysis.')) {
      return;
    }

    try {
      setAnalysisRunning(true);
      const response = await fetch('/api/kb/reanalyze-changed', {
        method: 'POST',
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setChangedFiles(null);
        fetchStats();
      } else {
        toast.error(data.error || 'Failed to start re-analysis');
      }
    } catch (error) {
      console.error('Error triggering re-analysis:', error);
      toast.error('Failed to trigger re-analysis');
    } finally {
      setAnalysisRunning(false);
    }
  };

  const smartUpdateAll = async () => {
    if (!window.confirm('Run Smart Update? This will:\n\n1. Analyze changed/new files\n2. Update module KBs\n3. Update platform overview\n4. Update infrastructure (DB + nginx)\n\nThis runs in the background.')) {
      return;
    }

    try {
      setAnalysisRunning(true);
      const response = await fetch('/api/kb/smart-update', {
        method: 'POST',
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Smart Update started! Running in background...');
        // Refresh stats after a delay
        setTimeout(() => {
          fetchStats();
          fetchModules();
        }, 5000);
      } else {
        toast.error(data.error || 'Failed to start smart update');
      }
    } catch (error) {
      console.error('Error starting smart update:', error);
      toast.error('Failed to start smart update');
    } finally {
      setAnalysisRunning(false);
    }
  };

  const fetchNewFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/kb/new-files', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setNewFiles(data.new_files);
        toast.success(`Found ${data.total} new files`);
      }
    } catch (error) {
      console.error('Error checking new files:', error);
      toast.error('Failed to check for new files');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformOverview = async () => {
    try {
      const response = await fetch('/api/kb/platform', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setPlatformOverview(data.platform);
      }
    } catch (error) {
      console.error('Error fetching platform overview:', error);
    }
  };

  const fetchInfrastructure = async () => {
    try {
      const response = await fetch('/api/kb/infrastructure', { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setInfrastructure(data.infrastructure);
      }
    } catch (error) {
      console.error('Error fetching infrastructure:', error);
    }
  };

  const generateInfrastructure = async () => {
    if (!window.confirm('Generate infrastructure KB? This will extract database schema and nginx config.')) {
      return;
    }

    try {
      setInfraGenerating(true);
      const response = await fetch('/api/kb/infrastructure/generate', {
        method: 'POST',
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchInfrastructure();
        fetchModules(); // Refresh modules list
      } else {
        toast.error(data.error || 'Failed to generate infrastructure KB');
      }
    } catch (error) {
      console.error('Error generating infrastructure:', error);
      toast.error('Failed to generate infrastructure KB');
    } finally {
      setInfraGenerating(false);
    }
  };

  const triggerAnalysis = async (phase) => {
    if (!window.confirm(`Start Phase ${phase} analysis? This may take several minutes.`)) {
      return;
    }

    try {
      setAnalysisRunning(true);
      const response = await fetch('/api/kb/trigger-analysis', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ phase })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.error || 'Failed to start analysis');
      }
    } catch (error) {
      console.error('Error triggering analysis:', error);
      toast.error('Failed to trigger analysis');
    } finally {
      setAnalysisRunning(false);
    }
  };

  const viewModuleDetail = async (moduleId) => {
    try {
      const response = await fetch(`/api/kb/modules/${moduleId}`, { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setSelectedModule(data);
      }
    } catch (error) {
      console.error('Error fetching module detail:', error);
    }
  };

  const viewFileDetail = async (fileId) => {
    try {
      const response = await fetch(`/api/kb/files/${fileId}`, { headers: authHeaders });
      const data = await response.json();
      if (data.success) {
        setSelectedFile(data);
      }
    } catch (error) {
      console.error('Error fetching file detail:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'files') {
      fetchFiles();
    } else if (activeTab === 'platform') {
      fetchPlatformOverview();
    } else if (activeTab === 'infrastructure') {
      fetchInfrastructure();
    } else if (activeTab === 'inference') {
      loadInferenceModules();
    }
  }, [activeTab, filterModule, filterStatus, searchTerm]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // ============================================================
  // AI INFERENCE FUNCTIONS
  // ============================================================

  const loadInferenceModules = async () => {
    try {
      const response = await api.get('/api/kb/inference/modules');
      // Note: api interceptor returns response.data directly
      if (response?.success) {
        setInferenceModules(response.modules || []);
      } else if (response?.modules) {
        // Direct response format
        setInferenceModules(response.modules || []);
      }
    } catch (err) {
      console.error('Failed to load inference modules:', err);
      // Don't block the UI, just log the error
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setInferenceLoading(true);
    setInferenceError(null);

    setChatMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const response = await api.post('/api/kb/inference/chat', {
        message: userMessage,
        conversation_id: conversationId,
        use_tools: true  // Use tool-based approach
      });

      // Debug: log the full response
      console.log('KB Inference Response:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);

      // Note: api interceptor returns response.data directly
      if (response && response.success) {
        const data = response.data;
        console.log('Answer:', data?.answer?.substring(0, 100));
        if (data && data.conversation_id) {
          setConversationId(data.conversation_id);
        }
        if (data && data.answer) {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: data.answer,
            timestamp: new Date().toISOString(),
            relevant_modules: data.relevant_modules || [],
            tool_calls: data.tool_calls || [],  // Tools the AI called
            mode: data.mode,  // 'tools' or 'context'
            tokens_used: response.tokens_used
          }]);
        } else {
          setInferenceError('No answer received from AI');
        }
      } else {
        console.error('Response not successful:', response);
        setInferenceError(response?.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('KB Inference error:', err);
      setInferenceError(err.response?.data?.detail || err.message || 'Failed to send message');
    } finally {
      setInferenceLoading(false);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setConversationId(null);
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const performInferenceSearch = async () => {
    if (!inferenceSearchQuery.trim()) return;

    setInferenceLoading(true);
    setInferenceError(null);

    try {
      const response = await api.post('/api/kb/inference/search', {
        query: inferenceSearchQuery,
        search_type: inferenceSearchType,
        limit: 20,
        include_content: false
      });

      // Note: api interceptor returns response.data directly
      if (response.success) {
        setInferenceSearchResults(response.data);
      }
    } catch (err) {
      setInferenceError(err.response?.data?.detail || 'Search failed');
    } finally {
      setInferenceLoading(false);
    }
  };

  const loadInferenceModuleDetails = async (moduleName) => {
    setInferenceLoading(true);
    setInferenceError(null);
    setSelectedInferenceModule(moduleName);

    try {
      const response = await api.get(`/api/kb/inference/module/${moduleName}`);
      // Note: api interceptor returns response.data directly
      if (response.success) {
        setInferenceModuleDetails(response.module);
      }
    } catch (err) {
      setInferenceError(err.response?.data?.detail || 'Failed to load module');
    } finally {
      setInferenceLoading(false);
    }
  };

  const analyzeImpact = async () => {
    if (!impactFiles.trim()) return;

    setInferenceLoading(true);
    setInferenceError(null);
    setImpactResult(null);

    const filePaths = impactFiles.split('\n').filter(f => f.trim());

    try {
      const response = await api.post('/api/kb/inference/impact', {
        file_paths: filePaths
      });

      // Note: api interceptor returns response.data directly
      if (response.success) {
        setImpactResult(response.data);
      } else {
        setInferenceError(response.error);
      }
    } catch (err) {
      setInferenceError(err.response?.data?.detail || 'Impact analysis failed');
    } finally {
      setInferenceLoading(false);
    }
  };

  const getSuggestion = async () => {
    if (!featureDescription.trim()) return;

    setInferenceLoading(true);
    setInferenceError(null);
    setSuggestion(null);

    try {
      const response = await api.post('/api/kb/inference/suggest/implementation', null, {
        params: {
          feature_description: featureDescription,
          target_module: targetModule || undefined
        }
      });

      // Note: api interceptor returns response.data directly
      if (response.success) {
        setSuggestion(response.data);
      } else {
        setInferenceError(response.error);
      }
    } catch (err) {
      setInferenceError(err.response?.data?.detail || 'Failed to get suggestion');
    } finally {
      setInferenceLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtext }) => (
    <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <Icon className={`w-8 h-8 opacity-50`} style={{ color }} />
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Files Analyzed"
            value={stats.files?.total || 0}
            icon={FileText}
            color="#3B82F6"
            subtext={`${stats.files?.analyzed || 0} completed`}
          />
          <StatCard
            title="Modules Generated"
            value={stats.modules?.total || 0}
            icon={FolderTree}
            color="#10B981"
          />
          <StatCard
            title="Stale Files"
            value={stats.files?.stale || 0}
            icon={AlertTriangle}
            color="#F59E0B"
            subtext="Need re-analysis"
          />
          <StatCard
            title="Failed Analysis"
            value={stats.files?.failed || 0}
            icon={AlertTriangle}
            color="#EF4444"
          />
        </div>
      )}

      {/* Analysis Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Analysis Controls</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => triggerAnalysis(1)}
            disabled={analysisRunning}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            Phase 1: File Analysis
          </button>
          <button
            onClick={() => triggerAnalysis(2)}
            disabled={analysisRunning}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            Phase 2: Module Discovery
          </button>
          <button
            onClick={() => triggerAnalysis(3)}
            disabled={analysisRunning}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            Phase 3: Platform Overview
          </button>
        </div>
      </div>

      {/* Smart Update - Primary Action */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">🚀 Smart Update</h3>
            <p className="text-blue-100 text-sm">
              One-click update: Analyzes changed files, updates modules, platform overview, and infrastructure.
            </p>
          </div>
          <button
            onClick={smartUpdateAll}
            disabled={analysisRunning}
            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 disabled:opacity-50 shadow-md"
          >
            {analysisRunning ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Update All
              </>
            )}
          </button>
        </div>
      </div>

      {/* Change Detection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Change Detection</h3>
        <div className="flex gap-4 mb-4">
          <button
            onClick={fetchChangedFiles}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            <GitCompare className="w-4 h-4" />
            Check Changed Files
          </button>
          <button
            onClick={fetchNewFiles}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            <FileText className="w-4 h-4" />
            Find New Files
          </button>
          <button
            onClick={() => { fetchStats(); fetchModules(); }}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Stats
          </button>
        </div>

        {changedFiles && (
          <div className="mt-4 p-4 bg-amber-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-amber-800">Change Detection Results</h4>
              {changedFiles.summary.changed > 0 && (
                <button
                  onClick={reanalyzeChangedFiles}
                  disabled={analysisRunning}
                  className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  Re-analyze {changedFiles.summary.changed} Changed Files Only
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500">Checked:</span> {changedFiles.summary.total_checked}</div>
              <div><span className="text-amber-600 font-medium">Changed:</span> {changedFiles.summary.changed}</div>
              <div><span className="text-red-600 font-medium">Deleted:</span> {changedFiles.summary.deleted}</div>
              <div><span className="text-green-600">Unchanged:</span> {changedFiles.summary.unchanged}</div>
            </div>
            {changedFiles.changed_files.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-amber-700">Changed Files:</p>
                <ul className="mt-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
                  {changedFiles.changed_files.slice(0, 20).map((f, i) => (
                    <li key={i} className="py-1 border-b border-amber-100">
                      {f.file_path.replace('/opt/SankalvaX-Platform/', '')}
                    </li>
                  ))}
                  {changedFiles.changed_files.length > 20 && (
                    <li className="py-1 text-amber-600">...and {changedFiles.changed_files.length - 20} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {newFiles && newFiles.length > 0 && (
          <div className="mt-4 p-4 bg-teal-50 rounded-lg">
            <h4 className="font-medium text-teal-800 mb-2">New Files Found: {newFiles.length}</h4>
            <ul className="text-xs text-gray-600 max-h-40 overflow-y-auto">
              {newFiles.slice(0, 20).map((f, i) => (
                <li key={i} className="py-1 border-b border-teal-100">
                  {f.file_path.replace('/opt/SankalvaX-Platform/', '')} ({f.file_type})
                </li>
              ))}
              {newFiles.length > 20 && (
                <li className="py-1 text-teal-600">...and {newFiles.length - 20} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Files by Type */}
      {stats?.by_type && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Files by Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {stats.by_type.map((item, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-lg font-bold">{item.count}</p>
                <p className="text-sm text-gray-500">.{item.file_type}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderModules = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Knowledge Base Modules ({modules.length})</h3>
        </div>
        <div className="divide-y">
          {modules.map((module) => (
            <div key={module.module_id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <FolderTree className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium">{module.module_name}</p>
                    <p className="text-sm text-gray-500 truncate max-w-xl">{module.purpose}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                  {module.file_count} files
                </span>
                <span className={`px-2 py-1 text-xs rounded ${
                  module.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {module.status}
                </span>
                <button
                  onClick={() => viewModuleDetail(module.module_id)}
                  className="p-2 hover:bg-gray-200 rounded"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Module Detail Modal */}
      {selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{selectedModule.module.module_name}</h3>
              <button onClick={() => setSelectedModule(null)} className="text-gray-500 hover:text-gray-700">
                &times;
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="mb-4">
                <h4 className="font-medium text-gray-700">Purpose</h4>
                <p className="text-sm text-gray-600">{selectedModule.module.purpose}</p>
              </div>
              <div className="mb-4">
                <h4 className="font-medium text-gray-700">Associated Files ({selectedModule.files.length})</h4>
                <ul className="mt-2 text-sm">
                  {selectedModule.files.map((f, i) => (
                    <li key={i} className="py-1 border-b border-gray-100">
                      <span className="font-mono text-xs">{f.file_path.replace('/opt/SankalvaX-Platform/', '')}</span>
                      <span className="ml-2 text-gray-400">- {f.purpose}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {selectedModule.yaml_content && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">YAML Content</h4>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto max-h-96">
                    {selectedModule.yaml_content}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderFiles = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Modules</option>
            {stats?.by_module?.map((m, i) => (
              <option key={i} value={m.predicted_module}>{m.predicted_module} ({m.count})</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="stale">Stale</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Files List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Analyzed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-sm">{file.file_name}</p>
                      <p className="text-xs text-gray-400 truncate max-w-xs">
                        {file.file_path.replace('/opt/SankalvaX-Platform/', '')}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded">
                      {file.predicted_module || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${
                      file.analysis_status === 'completed' ? 'bg-green-100 text-green-700' :
                      file.analysis_status === 'stale' ? 'bg-amber-100 text-amber-700' :
                      file.analysis_status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {file.analysis_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {file.analyzed_at ? new Date(file.analyzed_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => viewFileDetail(file.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* File Detail Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{selectedFile.file.file_name}</h3>
              <button onClick={() => setSelectedFile(null)} className="text-gray-500 hover:text-gray-700">
                &times;
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-medium text-gray-700">Path</h4>
                  <p className="text-sm text-gray-600 font-mono">{selectedFile.file.file_path}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700">Predicted Module</h4>
                  <p className="text-sm text-gray-600">{selectedFile.file.predicted_module} ({(selectedFile.file.module_confidence * 100).toFixed(0)}%)</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700">Purpose</h4>
                  <p className="text-sm text-gray-600">{selectedFile.file.purpose}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700">File Hash</h4>
                  <p className="text-sm text-gray-600 font-mono truncate">{selectedFile.file.file_hash}</p>
                </div>
              </div>
              {selectedFile.yaml_content && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">YAML Content</h4>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto max-h-96">
                    {selectedFile.yaml_content}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPlatform = () => (
    <div className="space-y-4">
      {platformOverview ? (
        <>
          {/* Platform Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold">{platformOverview.platform?.name || `${getBrandName()} Platform`}</h2>
            <p className="mt-2 opacity-90">{platformOverview.overview?.purpose?.split('\n')[0]}</p>
            <div className="mt-4 flex gap-4 text-sm">
              <span>Total Modules: {platformOverview.modules?.total || platformOverview.modules?.list?.length}</span>
              <span>Total Files: {platformOverview.stats?.total_files}</span>
            </div>
          </div>

          {/* Tech Stack */}
          {platformOverview.overview?.tech_stack && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Technology Stack</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(platformOverview.overview.tech_stack).map(([key, value]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 capitalize">{key.replace('_', ' ')}</p>
                    <p className="font-medium text-sm">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Module List */}
          {platformOverview.modules?.list && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Platform Modules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {platformOverview.modules.list.map((mod, i) => (
                  <div key={i} className="p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{mod.name}</span>
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">{mod.files} files</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{mod.one_liner}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Architecture Graph */}
          {platformOverview.module_graph?.graph_visualization && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Architecture Diagram</h3>
              <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded overflow-x-auto font-mono">
                {platformOverview.module_graph.graph_visualization}
              </pre>
            </div>
          )}

          {/* Recommendations */}
          {platformOverview.stats?.recommendations && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Improvement Recommendations</h3>
              <ul className="space-y-2">
                {platformOverview.stats.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Database className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Platform overview not generated yet.</p>
          <button
            onClick={() => triggerAnalysis(3)}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Generate Platform Overview
          </button>
        </div>
      )}
    </div>
  );

  const renderInfrastructure = () => (
    <div className="space-y-6">
      {/* Generate Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Infrastructure Knowledge Base</h3>
            <p className="text-sm text-gray-500">Database schema and nginx configuration</p>
          </div>
          <button
            onClick={generateInfrastructure}
            disabled={infraGenerating}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${infraGenerating ? 'animate-spin' : ''}`} />
            {infraGenerating ? 'Generating...' : 'Generate/Update Infrastructure KB'}
          </button>
        </div>
      </div>

      {infrastructure ? (
        <>
          {/* Database Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <Table2 className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold">Database Schema</h3>
                <p className="text-sm text-gray-500">
                  {infrastructure.database?.name} - {infrastructure.database?.table_count} tables
                </p>
              </div>
            </div>

            {/* Table List */}
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Table</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Columns</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Rows</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Primary Key</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Foreign Keys</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {infrastructure.database?.tables?.map((table, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{table.name}</td>
                      <td className="px-3 py-2">{table.column_count}</td>
                      <td className="px-3 py-2">{table.row_count?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {table.primary_keys?.join(', ') || '-'}
                      </td>
                      <td className="px-3 py-2">
                        {table.foreign_keys?.length > 0 ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {table.foreign_keys.length} FK
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Relationships */}
            {infrastructure.database?.relationships?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700 mb-2">Table Relationships ({infrastructure.database.relationships.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {infrastructure.database.relationships.map((rel, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-xs rounded">
                      {rel.from} → {rel.to}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Nginx Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <Server className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold">Nginx Configuration</h3>
                <p className="text-sm text-gray-500">{infrastructure.nginx?.config_path}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Server Names</p>
                <p className="font-medium">{infrastructure.nginx?.server_names?.join(', ') || '-'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Listen Ports</p>
                <p className="font-medium">{infrastructure.nginx?.listen_ports?.length || 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Locations</p>
                <p className="font-medium">{infrastructure.nginx?.locations?.length || 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">SSL Enabled</p>
                <p className="font-medium">{infrastructure.nginx?.ssl_enabled ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {/* Locations */}
            {infrastructure.nginx?.locations?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">URL Locations</h4>
                <div className="flex flex-wrap gap-2">
                  {infrastructure.nginx.locations.map((loc, i) => (
                    <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-mono">
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Full Config */}
            {infrastructure.nginx?.full_config && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Full Configuration</h4>
                <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto max-h-64 overflow-y-auto">
                  {infrastructure.nginx.full_config}
                </pre>
              </div>
            )}
          </div>

          {/* Deployment Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Deployment Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(infrastructure.deployment || {}).map(([key, value]) => (
                <div key={key} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-mono text-sm truncate" title={value}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Server className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Infrastructure KB not generated yet.</p>
          <button
            onClick={generateInfrastructure}
            disabled={infraGenerating}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Generate Infrastructure KB
          </button>
        </div>
      )}
    </div>
  );

  // ============================================================
  // AI INFERENCE TAB RENDER
  // ============================================================

  const renderInferenceChat = () => (
    <div className="flex flex-col h-[500px]">
      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 rounded-t-lg">
        {chatMessages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <Brain className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">Ask me about the codebase</h3>
            <p className="text-sm">I have knowledge of all {inferenceModules.length}+ modules</p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="text-gray-600">Try asking:</p>
              <button
                onClick={() => setChatInput("How does the bill processing workflow work?")}
                className="block mx-auto text-blue-600 hover:underline"
              >
                "How does the bill processing workflow work?"
              </button>
              <button
                onClick={() => setChatInput("What is the system architecture?")}
                className="block mx-auto text-blue-600 hover:underline"
              >
                "What is the system architecture?"
              </button>
              <button
                onClick={() => setChatInput("How do I add a new agent?")}
                className="block mx-auto text-blue-600 hover:underline"
              >
                "How do I add a new agent?"
              </button>
            </div>
          </div>
        )}

        {chatMessages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-3xl rounded-lg p-4 ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border shadow-sm'
              }`}
            >
              <div className="whitespace-pre-wrap">{msg.content}</div>

              {/* Show tool calls made by AI */}
              {msg.tool_calls && msg.tool_calls.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <span className="text-xs text-purple-600 font-medium">Tools called: </span>
                  <div className="mt-1 space-y-1">
                    {msg.tool_calls.map((tc, i) => (
                      <div key={i} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded flex items-center gap-2">
                        <Zap className="w-3 h-3" />
                        <span className="font-medium">{tc.tool}</span>
                        {tc.input && Object.keys(tc.input).length > 0 && (
                          <span className="text-purple-500">
                            ({Object.entries(tc.input).map(([k, v]) => `${k}: ${typeof v === 'string' ? v.substring(0, 30) : v}`).join(', ')})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {msg.relevant_modules && msg.relevant_modules.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-500">Related modules: </span>
                  {msg.relevant_modules.map((m, i) => (
                    <span
                      key={i}
                      className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded mr-1"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              )}
              {msg.tokens_used && (
                <div className="text-xs text-gray-400 mt-1">
                  Tokens: {msg.tokens_used}
                </div>
              )}
            </div>
          </div>
        ))}

        {inferenceLoading && (
          <div className="flex justify-start">
            <div className="bg-white border shadow-sm rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="text-gray-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t p-4 bg-white rounded-b-lg">
        {inferenceError && (
          <div className="mb-2 p-2 bg-red-50 text-red-600 text-sm rounded">
            {inferenceError}
          </div>
        )}
        <div className="flex items-end space-x-2">
          <textarea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={handleChatKeyPress}
            placeholder="Ask about the codebase..."
            className="flex-1 border rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={2}
            disabled={inferenceLoading}
          />
          <div className="flex flex-col space-y-2">
            <button
              onClick={sendChatMessage}
              disabled={inferenceLoading || !chatInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
            {chatMessages.length > 0 && (
              <button
                onClick={clearChat}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderInferenceSearch = () => (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex space-x-2">
        <input
          type="text"
          value={inferenceSearchQuery}
          onChange={(e) => setInferenceSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && performInferenceSearch()}
          placeholder="Search the knowledge base..."
          className="flex-1 border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={inferenceSearchType}
          onChange={(e) => setInferenceSearchType(e.target.value)}
          className="border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All</option>
          <option value="files">Files</option>
          <option value="modules">Modules</option>
        </select>
        <button
          onClick={performInferenceSearch}
          disabled={inferenceLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          Search
        </button>
      </div>

      {/* Results */}
      {inferenceSearchResults && (
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            Found {inferenceSearchResults.files?.length || 0} files and {inferenceSearchResults.modules?.length || 0} modules
          </div>

          {inferenceSearchResults.modules?.length > 0 && (
            <div>
              <h3 className="font-medium mb-2">Modules</h3>
              <div className="space-y-2">
                {inferenceSearchResults.modules.map((m, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50"
                    onClick={() => loadInferenceModuleDetails(m.module_name)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium text-blue-600">{m.module_name}</span>
                        <span className="text-sm text-gray-500 ml-2">({m.file_count} files)</span>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        {(m.relevance * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{m.purpose?.substring(0, 150)}...</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {inferenceSearchResults.files?.length > 0 && (
            <div>
              <h3 className="font-medium mb-2">Files</h3>
              <div className="space-y-2">
                {inferenceSearchResults.files.map((f, idx) => (
                  <div key={idx} className="p-3 bg-white border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{f.file_name}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded ml-2">
                          {f.file_type}
                        </span>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        {(f.relevance * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{f.purpose}</p>
                    <p className="text-xs text-gray-400 mt-1">{f.file_path}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderInferenceImpact = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Enter file paths (one per line) to analyze the impact of changes.
      </p>

      <textarea
        value={impactFiles}
        onChange={(e) => setImpactFiles(e.target.value)}
        placeholder="/opt/SankalvaX-Platform/Backend/agents/bill_processing/engine/workflow_engine.py"
        className="w-full border rounded-lg p-3 h-32 font-mono text-sm"
      />

      <button
        onClick={analyzeImpact}
        disabled={inferenceLoading || !impactFiles.trim()}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {inferenceLoading ? 'Analyzing...' : 'Analyze Impact'}
      </button>

      {impactResult && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-2">Analysis Result</h3>
          <div className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded max-h-96 overflow-y-auto">
            {impactResult.analysis}
          </div>
          {impactResult.tokens_used && (
            <div className="text-xs text-gray-400 mt-2">
              Tokens used: {impactResult.tokens_used}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderInferenceSuggest = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Describe a feature and get AI-powered implementation suggestions based on the codebase.
      </p>

      <div>
        <label className="block text-sm font-medium mb-1">Feature Description</label>
        <textarea
          value={featureDescription}
          onChange={(e) => setFeatureDescription(e.target.value)}
          placeholder="I want to add a new agent that processes customer refunds..."
          className="w-full border rounded-lg p-3 h-32"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Target Module (optional)</label>
        <select
          value={targetModule}
          onChange={(e) => setTargetModule(e.target.value)}
          className="w-full border rounded-lg p-3"
        >
          <option value="">Auto-detect</option>
          {inferenceModules.map((m, idx) => (
            <option key={idx} value={m.name}>{m.name}</option>
          ))}
        </select>
      </div>

      <button
        onClick={getSuggestion}
        disabled={inferenceLoading || !featureDescription.trim()}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {inferenceLoading ? 'Generating...' : 'Get Suggestion'}
      </button>

      {suggestion && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-2">Implementation Suggestion</h3>
          {suggestion.relevant_modules?.length > 0 && (
            <div className="mb-2">
              <span className="text-sm text-gray-500">Relevant modules: </span>
              {suggestion.relevant_modules.map((m, i) => (
                <span key={i} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded mr-1">
                  {m}
                </span>
              ))}
            </div>
          )}
          <div className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded max-h-96 overflow-y-auto">
            {suggestion.suggestion}
          </div>
        </div>
      )}
    </div>
  );

  const renderInference = () => (
    <div className="space-y-6">
      {/* Sub-tabs for inference */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b px-4">
          <div className="flex space-x-4">
            {[
              { id: 'chat', label: 'Chat', icon: MessageSquare },
              { id: 'search', label: 'Search', icon: Search },
              { id: 'impact', label: 'Impact Analysis', icon: Zap },
              { id: 'suggest', label: 'Suggest', icon: Lightbulb }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setInferenceSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  inferenceSubTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {inferenceSubTab === 'chat' && renderInferenceChat()}
          {inferenceSubTab === 'search' && renderInferenceSearch()}
          {inferenceSubTab === 'impact' && renderInferenceImpact()}
          {inferenceSubTab === 'suggest' && renderInferenceSuggest()}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Database },
    { id: 'modules', label: 'Modules', icon: FolderTree },
    { id: 'files', label: 'Files', icon: FileText },
    { id: 'infrastructure', label: 'Infrastructure', icon: Server },
    { id: 'platform', label: 'Platform', icon: Eye },
    { id: 'inference', label: 'AI Inference', icon: Brain },
    { id: 'migration', label: 'Migration', icon: Copy },
    { id: 'deployment', label: 'Instance Deployment', icon: Server },
  ];

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Knowledge Base Admin</h1>
          <p className="text-gray-500">Manage and monitor the AI-generated codebase knowledge base</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b">
          <div className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'modules' && renderModules()}
        {activeTab === 'files' && renderFiles()}
        {activeTab === 'infrastructure' && renderInfrastructure()}
        {activeTab === 'platform' && renderPlatform()}
        {activeTab === 'inference' && renderInference()}
        {activeTab === 'migration' && <MigrationTab />}
        {activeTab === 'deployment' && <InstanceDeploymentTab />}
      </div>
    </MainLayout>
  );
};

export default KnowledgeBaseAdmin;
